import type { TimelineItem } from "@hali-shares/deck-schema";
import { RefsPicker, Select, TextArea, TextField, UpIcon, XIcon, move } from "../fields";
import { uniqueId } from "../model";
import type { B, EditorProps } from "./types";

const MAX = 6;

/** Points oldest first: when, label and a line on what changed, each with its own footnotes. Same rows as flow steps. */
export function TimelineEditor({ value: b, onChange, deck }: EditorProps<B<"timeline">>) {
  const set = (patch: Partial<B<"timeline">>) => onChange({ ...b, ...patch });
  const setItem = (i: number, patch: Partial<TimelineItem>) =>
    set({ items: b.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const add = () => {
    const id = uniqueId(
      "point",
      b.items.map((i) => i.id),
    );
    set({ items: [...b.items, { id, when: "", label: "", refs: [] }] });
  };
  const remove = (i: number) => {
    const gone = b.items[i]!.id;
    set({ items: b.items.filter((_, j) => j !== i), ...(b.emphasis === gone && { emphasis: undefined }) });
  };

  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => set({ title })} />
      <TextArea label="Lead" max={240} value={b.lead} onChange={(lead) => set({ lead: lead || undefined })} />

      <div className="field">
        <div className="field-label">
          <span>Points, oldest first</span>
          <span className="field-count">
            {b.items.length}/{MAX}
          </span>
        </div>
        <ol className="flow-steps">
          {b.items.map((it, i) => (
            <li key={it.id} className="flow-step">
              <div className="flow-step-row tl-row">
                <span className="flow-step-num">{i + 1}</span>
                <input
                  className="input input-mono"
                  aria-label={`Point ${i + 1} when`}
                  placeholder="2024"
                  maxLength={16}
                  value={it.when}
                  onChange={(e) => setItem(i, { when: e.target.value })}
                />
                <input
                  className="input"
                  aria-label={`Point ${i + 1} label`}
                  placeholder="What happened"
                  maxLength={40}
                  value={it.label}
                  onChange={(e) => setItem(i, { label: e.target.value })}
                />
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move point ${i + 1} up`}
                  disabled={i === 0}
                  onClick={() => set({ items: move(b.items, i, i - 1) })}
                >
                  <UpIcon />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move point ${i + 1} down`}
                  disabled={i === b.items.length - 1}
                  onClick={() => set({ items: move(b.items, i, i + 1) })}
                >
                  <UpIcon down />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Remove point ${i + 1}`}
                  disabled={b.items.length <= 2}
                  onClick={() => remove(i)}
                >
                  <XIcon />
                </button>
              </div>
              <div className="flow-step-detail">
                <TextArea
                  label={`Point ${i + 1} caption`}
                  max={140}
                  value={it.caption}
                  onChange={(caption) => setItem(i, { caption: caption || undefined })}
                />
                <RefsPicker
                  label={`Point ${i + 1} footnotes`}
                  references={deck.references}
                  value={it.refs}
                  onChange={(refs) => setItem(i, { refs })}
                />
              </div>
            </li>
          ))}
        </ol>
        {b.items.length < MAX && (
          <button type="button" className="s-btn s-btn-dashed" style={{ alignSelf: "flex-start" }} onClick={add}>
            + Point
          </button>
        )}
      </div>

      <div className="grid-2">
        <Select
          label="Show"
          value={b.reveal}
          options={[
            { value: "steps", label: "One at a time (Next reveals each)" },
            { value: "all", label: "All at once" },
          ]}
          onChange={(reveal) => set({ reveal })}
        />
        {b.reveal === "all" && (
          <Select
            label="Highlighted point"
            value={b.emphasis ?? ""}
            options={[
              { value: "", label: "None" },
              ...b.items.map((it, i) => ({ value: it.id, label: `${i + 1}. ${it.label || it.when || "Untitled"}` })),
            ]}
            onChange={(emphasis) => set({ emphasis: emphasis || undefined })}
          />
        )}
      </div>
      <RefsPicker label="Footnotes for the whole slide" references={deck.references} value={b.refs} onChange={(refs) => set({ refs })} />
    </>
  );
}
